import React from "react"
import { Outlet, RouteObject } from "react-router"
import { eproModuleRouteNames } from "src/pages/epro-module/epro-module.route-names"
import { css } from "@emotion/react"
import { createRedirect, Redirect } from "src/router/routes"
import CheckValidVaultSession from "src/check-valid-vault-session"

export interface RouteRedirectOnStudyLockedHandle {
  redirectOnStudyLocked: (params: {
    vaultId: string
    globalSponsorStudyId: string
    collectionId: string
  }) => string
}

// collections
const Fallback = (
  <div
    css={(theme) => css`
      height: 100%;
      background: ${theme.palette.background.neutral.alt};
    `}
  />
)
const suspenseRoute = (
  fallback: React.ReactNode,
  routes: RouteObject | RouteObject[]
): RouteObject => {
  return {
    element: (
      <React.Suspense fallback={fallback}>
        <Outlet />
      </React.Suspense>
    ),
    children: Array.isArray(routes) ? routes : [routes],
  }
}

export const createCollectionRoutes = (): RouteObject =>
  suspenseRoute(Fallback, {
    path: eproModuleRouteNames.collection.route,
    lazy: () => import("src/pages/epro-module/collection/collection"),
    children: [
      {
        path: eproModuleRouteNames.collectionStudyDetails.route,
        lazy: () => import("src/pages/epro-module/collection/details/details"),
      },
      {
        path: eproModuleRouteNames.collectionScreeningId.route,
        lazy: () =>
          import("src/pages/epro-module/collection/screening-id/screening-id"),
      },
      {
        path: eproModuleRouteNames.collectionSurveys.route,
        lazy: () => import("src/pages/epro-module/collection/surveys/surveys"),
      },
      {
        path: eproModuleRouteNames.collectionLanguages.route,
        lazy: () =>
          import("src/pages/epro-module/collection/languages/languages"),
      },
      {
        path: eproModuleRouteNames.collectionGroups.route,
        lazy: () => import("src/pages/epro-module/collection/groups/groups"),
      },
      {
        path: eproModuleRouteNames.collectionEvents.route,
        lazy: () => import("src/pages/epro-module/collection/events/events"),
      },
      {
        path: eproModuleRouteNames.collectionRules.route,
        lazy: () => import("src/pages/epro-module/collection/rules/rules"),
      },
      createRedirect({
        index: true,
        to: eproModuleRouteNames.collectionSurveys.route,
      }),
      createRedirect({
        from: "*",
        to: eproModuleRouteNames.collectionSurveys.route,
      }),
    ],
  })

export const createStudySettingsRoutes = (): RouteObject => ({
  lazy: () =>
    import("src/pages/epro-module/page-layouts/study-settings-layout"),
  children: [
    {
      children: [
        // grouping these in case they get a shared error element later
        {
          path: eproModuleRouteNames.createFtp.route,
          lazy: async () =>
            import("src/pages/epro-module/study-settings/study-export-jobs/create-ftp"),
          handle: {
            redirectOnStudyLocked: eproModuleRouteNames.studyExportJobs,
          } satisfies RouteRedirectOnStudyLockedHandle,
        },
        {
          path: eproModuleRouteNames.editFtp.route,
          lazy: async () =>
            import("src/pages/epro-module/study-settings/study-export-jobs/edit-ftp"),
        },
        {
          path: eproModuleRouteNames.createCdb.route,
          lazy: async () =>
            import("src/pages/epro-module/study-settings/study-export-jobs/create-cdb"),
          handle: {
            redirectOnStudyLocked: eproModuleRouteNames.studyExportJobs,
          } satisfies RouteRedirectOnStudyLockedHandle,
        },
        {
          path: eproModuleRouteNames.editCdb.route,
          lazy: async () =>
            import("src/pages/epro-module/study-settings/study-export-jobs/edit-cdb"),
        },
      ],
    },
  ],
})

export const createStudyExportsRoutes = (): RouteObject => ({
  lazy: () =>
    import("src/pages/epro-module/page-layouts/study-settings-layout"),
  children: [
    {
      children: [
        {
          path: eproModuleRouteNames.createCustomExport.route,
          lazy: async () =>
            import("src/pages/epro-module/exports/create-custom-export"),
          handle: {
            redirectOnStudyLocked: eproModuleRouteNames.studyCustomExports,
          } satisfies RouteRedirectOnStudyLockedHandle,
        },
        {
          path: eproModuleRouteNames.editCustomExport.route,
          lazy: async () =>
            import("src/pages/epro-module/exports/edit-custom-export"),
          handle: {
            redirectOnStudyLocked: eproModuleRouteNames.studyCustomExports,
          } satisfies RouteRedirectOnStudyLockedHandle,
        },
        {
          path: eproModuleRouteNames.previewCustomExport.route,
          lazy: async () =>
            import("src/pages/epro-module/exports/custom-export-preview-page"),
        },
      ],
    },
  ],
})

export const createStudioRoutes = (): RouteObject[] => {
  return [
    createStudySettingsRoutes(),
    createStudyExportsRoutes(),
    createCollectionRoutes(),
    {
      lazy: () =>
        import("src/pages/epro-module/page-layouts/base-collection-layout"),
      children: [
        {
          children: [
            {
              path: eproModuleRouteNames.collectionAddSurveyFromLibrary.route,
              lazy: () =>
                import("src/pages/epro-module/collection/surveys/add-from-library"),
              handle: {
                redirectOnStudyLocked: eproModuleRouteNames.collectionSurveys,
              } satisfies RouteRedirectOnStudyLockedHandle,
            },
            {
              path: eproModuleRouteNames.createSurveyRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/survey-rule-template/create-survey-rule"),
              handle: {
                redirectOnStudyLocked: eproModuleRouteNames.collectionRules,
              } satisfies RouteRedirectOnStudyLockedHandle,
            },
            {
              path: eproModuleRouteNames.editSurveyRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/survey-rule-template/edit-survey-rule"),
            },
            {
              path: eproModuleRouteNames.createCriteriaCheckRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/criteria-check-rule-template/create-criteria-check-rule"),
              handle: {
                redirectOnStudyLocked: eproModuleRouteNames.collectionRules,
              } satisfies RouteRedirectOnStudyLockedHandle,
            },
            {
              path: eproModuleRouteNames.editCriteriaCheckRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/criteria-check-rule-template/edit-criteria-check-rule"),
            },
            {
              path: eproModuleRouteNames.createScheduleTriggerRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/schedule-trigger-rule-template/create-schedule-trigger-rule"),
              handle: {
                redirectOnStudyLocked: eproModuleRouteNames.collectionRules,
              } satisfies RouteRedirectOnStudyLockedHandle,
            },
            {
              path: eproModuleRouteNames.editScheduleTriggerRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/schedule-trigger-rule-template/edit-schedule-trigger-rule"),
            },
            {
              path: eproModuleRouteNames.createPopulateEventRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/populate-event-rule-template/create-populate-event-rule"),
              handle: {
                redirectOnStudyLocked: eproModuleRouteNames.collectionRules,
              } satisfies RouteRedirectOnStudyLockedHandle,
            },
            {
              path: eproModuleRouteNames.editPopulateEventRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/populate-event-rule-template/edit-populate-event-rule"),
            },
            {
              path: eproModuleRouteNames.createSurveyTriggeredApiRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/survey-triggered-api-rule-template/create-survey-triggered-api-rule"),
              handle: {
                redirectOnStudyLocked: eproModuleRouteNames.collectionRules,
              } satisfies RouteRedirectOnStudyLockedHandle,
            },
            {
              path: eproModuleRouteNames.editSurveyTriggeredApiRule.route,
              lazy: () =>
                import("src/pages/epro-module/collection/rules/survey-triggered-api-rule-template/edit-survey-triggered-api-rule"),
            },
          ],
        },
        {
          path: eproModuleRouteNames.studio.route,
          element: <Redirect to={eproModuleRouteNames.collections.route} />,
        },
      ],
    },
    {
      path: eproModuleRouteNames.studyDeploymentsBulkDeploy.route,
      lazy: async () =>
        import("src/pages/epro-module/study-settings/study-deployments/bulk-deploy-sites/bulk-deploy-sites"),
    },
    {
      lazy: async () =>
        import("src/pages/epro-module/page-layouts/studio-nav-layout"),
      children: [
        suspenseRoute(Fallback, {
          path: eproModuleRouteNames.collections.route,
          lazy: () => import("src/pages/epro-module/collections/collections"),
        }),
        {
          path: eproModuleRouteNames.studySettings.route,
          lazy: async () =>
            import("src/pages/epro-module/study-settings/study-settings-sidenav"),
          children: [
            {
              path: eproModuleRouteNames.studyLanguages.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-languages/study-languages"),
            },
            {
              path: eproModuleRouteNames.studyResources.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-resources/study-resources"),
            },
            {
              path: eproModuleRouteNames.studyDeployments.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-deployments/study-deployments"),
            },
            {
              path: eproModuleRouteNames.studyDataChanges.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-data-changes/study-data-changes"),
            },
            {
              path: eproModuleRouteNames.studySiteSignature.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-site-signature/study-site-signature"),
            },
            {
              path: eproModuleRouteNames.studyParticipantId.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-participant-id-format/study-participant-id-format"),
            },
            {
              path: eproModuleRouteNames.inPersonAccess.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/in-person-access/in-person-access"),
            },
            {
              index: true,
              element: (
                <Redirect to={eproModuleRouteNames.studyDeployments.route} />
              ),
            },
          ],
        },
        {
          path: eproModuleRouteNames.studyExports.route,
          lazy: async () =>
            import("src/pages/epro-module/exports/exports-sidenav"),
          children: [
            {
              index: true,
              element: (
                <Redirect to={eproModuleRouteNames.studyCustomExports.route} />
              ),
            },
            {
              path: eproModuleRouteNames.studyCustomExports.route,
              lazy: async () =>
                import("src/pages/epro-module/exports/study-custom-exports"),
            },
            {
              path: eproModuleRouteNames.studyExportJobs.route,
              lazy: async () =>
                import("src/pages/epro-module/study-settings/study-export-jobs/study-export-jobs"),
            },
            {
              path: eproModuleRouteNames.outboundApis.route,
              lazy: async () =>
                import("src/pages/epro-module/exports/outbound-apis/outbound-apis"),
            },
            {
              path: eproModuleRouteNames.complianceViews.route,
              lazy: async () =>
                import("src/pages/epro-module/exports/compliance-views/compliance-views"),
            },
          ],
        },
      ],
    },
  ]
}

export const createEproModuleRoutes = (): RouteObject[] => [
  {
    path: eproModuleRouteNames.studyPreview.route,
    lazy: () => import("src/pages/epro-module/preview/study-preview-page"),
  },
  {
    element: (
      <main
        css={css`
          height: 100%;
        `}
      >
        <Outlet />
      </main>
    ),
    children: [
      // authenticated routes
      {
        element: (
          <>
            <CheckValidVaultSession />
            <Outlet />
          </>
        ),
        children: [
          ...createStudioRoutes(),
          {
            path: eproModuleRouteNames.dynamicEproPreview.route,
            lazy: () => import("src/components/preview/epro-preview-page"),
          },
          {
            path: eproModuleRouteNames.libraryEproPreview.route,
            lazy: () => import("src/components/preview/epro-preview-page"),
          },
        ],
      },
      // manage survey routes (separate error handling)
      {
        children: [
          {
            path: eproModuleRouteNames.collectionEditSurvey.route,
            lazy: () =>
              import("src/pages/epro-module/collection/surveys/manage-collection-survey/edit-collection-survey"),
          },
          {
            path: eproModuleRouteNames.collectionCreateSurvey.route,
            lazy: () =>
              import("src/pages/epro-module/collection/surveys/manage-collection-survey/create-collection-survey"),
          },
        ],
      },
      // unauthenticated routes
      {
        children: [
          {
            path: eproModuleRouteNames.globalEproPreview.route,
            lazy: () => import("src/components/preview/epro-preview-page"),
          },
          {
            path: eproModuleRouteNames.DEPRECATED_eproPreviewV1.route,
            lazy: () =>
              import("src/pages/epro-module/redirect-preview-from-patients-to-sponsors"),
          },
          {
            path: eproModuleRouteNames.DEPRECATED_eproPreviewV2.route,
            lazy: () =>
              import("src/pages/epro-module/redirect-preview-from-patients-to-sponsors"),
          },
        ],
      },
    ],
  },
]
